import React, { useState, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import CallFlowModal from "./CallFlowModal";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack,
  LinearProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

import { baseURL } from "../backend_url";

// -------- helper functions --------
const fmtUTC = (val) =>
  val
    ? new Date(val).toLocaleString("en-GB", {
        timeZone: "UTC",
        hour12: false,
      }) + " UTC"
    : "-";

function normStatus(s) {
  if (!s) return "other";
  s = s.toLowerCase();
  if (s.includes("miss")) return "missed";
  if (s.includes("abandon") || s.includes("busy") || s.includes("no answer"))
    return "abandoned";
  if (s.includes("answer") || s === "completed") return "answered";
  return "other";
}

const statusColor = {
  answered: "success",
  missed: "error",
  abandoned: "warning",
  other: "default",
};

const secondsToHMS = (sec) => {
  const s = Number(sec || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h ? `${h}h ` : ""}${m ? `${m}m ` : ""}${s % 60}s`;
};

// ⭐ Row color styling
const getRowColor = (r) => {
  const status = normStatus(r.status);

  if (status === "answered") return { backgroundColor: "#e8f5e9" }; // green
  if (status === "missed" && r.actualMissed)
    return { backgroundColor: "#ffe0b2" }; // orange
  if (status === "missed" && !r.actualMissed)
    return { backgroundColor: "#ffebee" }; // light red
  if (status === "abandoned" || r.abandoned)
    return { backgroundColor: "#fff9c4" }; // yellow

  return {};
};

const formatTotalDuration = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${h ? `${h}h ` : ""}${m ? `${m}m ` : ""}${s}s`;
};

export default function VonageCallsInfo() {
  const [mode, setMode] = useState("agent"); // "agent" | "all_agents"
  const [agentId, setAgentId] = useState("");
  const [agents] = useState([
    { id: "9904", name: "Leela Prema" },
    { id: "9913", name: "Syed CTI0144" },
    { id: "9911", name: "Sheila Belinda Heathcote" },
    { id: "9908", name: "Harini Munusamy" },
    { id: "9914", name: "Nafees Hussain" },
    { id: "9906", name: "Manjula S" },
    { id: "9921", name: "Syed Umar" },
    { id: "9916", name: "Alice Jennifer S" },
    { id: "9917", name: "R. Raghu" },
    { id: "9925", name: "Yenugula lahanvitha" },
    { id: "9919", name: "Kalyan Verabhadra" },
    { id: "9926", name: "Chethan CJ" },
    { id: "9924", name: "B. Bhuvaneswari" },
    { id: "9923", name: "Punith HN" },
    { id: "9920", name: "Owaise Ahmed" },
  ]);

  const [phone, setPhone] = useState("");
  const [fromDate, setFromDate] = useState(
    new Date(Date.now() - 6 * 86400000).toISOString().split("T")[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [status, setStatus] = useState("all");

  const [rows, setRows] = useState([]); // ALL rows for current search (single agent or all agents)
  const [allAgentsData, setAllAgentsData] = useState({}); // { agentId: [rows...] } for mode === "all_agents"
  const [activeAgentTab, setActiveAgentTab] = useState("");

  const [searchText, setSearchText] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 15;

  // ⭐ Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  // ⭐ Validation error state
  const [fieldErrors, setFieldErrors] = useState({
    agentId: false,
    phone: false,
    fromDate: false,
    toDate: false,
  });

  const FS_AGENT = `${baseURL}/vonage/agent_calls_fs`;
  const FS_STATUS = (id) => `${baseURL}/vonage/agent_calls/status_fs/${id}`;
  const FS_CALLER = `${baseURL}/vonage/caller_history_fs`;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Which rows are the "current agent" rows (for summary/cards/chart/table)
  const summaryRows = useMemo(() => {
    if (mode === "all_agents" && activeAgentTab && allAgentsData[activeAgentTab]) {
      return allAgentsData[activeAgentTab];
    }
    return rows;
  }, [mode, activeAgentTab, allAgentsData, rows]);

  // ⭐ FILTER + SORT + SEARCH (for the table view)
  const filteredRows = useMemo(() => {
    const baseRows =
      mode === "all_agents" && activeAgentTab && allAgentsData[activeAgentTab]
        ? allAgentsData[activeAgentTab]
        : rows;

    let output = [...baseRows];

    // --- Status filter ---
    if (status === "answered") {
      output = output.filter((r) => normStatus(r.status) === "answered");
    } else if (status === "missed") {
      output = output.filter((r) => normStatus(r.status) === "missed");
    } else if (status === "actual_missed") {
      output = output.filter(
        (r) => normStatus(r.status) === "missed" && r.actualMissed === true
      );
    } else if (status === "abandoned") {
      output = output.filter((r) => r.abandoned === true);
    }

    // --- Text Search ---
    const term = searchText.toLowerCase();
    if (term) {
      output = output.filter((r) =>
        Object.values(r).some((v) => String(v).toLowerCase().includes(term))
      );
    }

    // --- Sorting ---
    if (sortField) {
      output.sort((a, b) => {
        let x = a[sortField];
        let y = b[sortField];

        // date fields
        if (["startedAt", "start", "end"].includes(sortField)) {
          x = x ? new Date(x).getTime() : 0;
          y = y ? new Date(y).getTime() : 0;
        }

        // numeric fields
        if (!isNaN(x) && !isNaN(y)) {
          x = Number(x);
          y = Number(y);
        }

        if (x < y) return sortOrder === "asc" ? -1 : 1;
        if (x > y) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return output;
  }, [
    rows,
    searchText,
    status,
    sortField,
    sortOrder,
    mode,
    activeAgentTab,
    allAgentsData,
  ]);

  const paginatedRows = filteredRows.slice((page - 1) * limit, page * limit);

  // ⭐ Chart data: per-day aggregate (for current agent / current view)
  const chartData = useMemo(() => {
    if (!summaryRows.length) return [];
    const map = {};

    summaryRows.forEach((r) => {
      const dt = r.startedAt || r.start || r.endedAt;
      if (!dt) return;
      const dateKey = new Date(dt).toISOString().split("T")[0];
      if (!map[dateKey]) {
        map[dateKey] = {
          date: dateKey,
          answered: 0,
          missed: 0,
          actualMissed: 0,
          abandoned: 0,
        };
      }

      const st = normStatus(r.status);
      if (st === "answered") {
        map[dateKey].answered += 1;
      } else if (st === "missed") {
        map[dateKey].missed += 1;
        if (r.actualMissed) map[dateKey].actualMissed += 1;
        if (r.abandoned) map[dateKey].abandoned += 1;
      } else if (st === "abandoned" || r.abandoned) {
        map[dateKey].abandoned += 1;
      }
    });

    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [summaryRows]);

  // SUMMARY FOR CURRENT VIEW (single agent or active tab)
  const totalReceived = useMemo(
    () => summaryRows.filter((r) => normStatus(r.status) === "answered").length,
    [summaryRows]
  );
  const totalMissed = useMemo(
    () => summaryRows.filter((r) => normStatus(r.status) === "missed").length,
    [summaryRows]
  );
  const totalActualMissed = useMemo(
    () =>
      summaryRows.filter(
        (r) => normStatus(r.status) === "missed" && r.actualMissed === true
      ).length,
    [summaryRows]
  );
  const totalAbandoned = useMemo(
    () => summaryRows.filter((r) => r.abandoned === true).length,
    [summaryRows]
  );

  const totalDuration = useMemo(
    () => summaryRows.reduce((sum, r) => sum + (Number(r.durationSec) || 0), 0),
    [summaryRows]
  );

  const [flowOpen, setFlowOpen] = useState(false);
  const [flowData, setFlowData] = useState([]);

  const showCallFlow = (phone, date) => {
    if (!phone || !date) return;

    // Filter all calls having same phone + same date
    const targetDate = new Date(date).toISOString().split("T")[0];

    const sameGroup = rows.filter((r) => {
        const rowDate = new Date(r.startedAt).toISOString().split("T")[0];
        return (
        rowDate === targetDate &&
        (String(r.from) === phone || String(r.to) === phone)
        );
    });

    // Sort by time
    sameGroup.sort((a, b) => new Date(a.startedAt) - new Date(b.startedAt));

    setFlowData(sameGroup);
    setFlowOpen(true);
 };

  const fmtTimeOnly = (val) =>
    val ? new Date(val).toLocaleTimeString("en-GB", { hour12: false }) : "-";

  const exportAllAgentsCSV = () => {
    if (mode !== "all_agents" || rows.length === 0) {
        Swal.fire("No data", "No rows available.", "info");
        return;
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(ws);

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "vonage_all_agents.csv");
  };

  const exportAllAgentsExcel = () => {
    if (mode !== "all_agents" || rows.length === 0) {
        Swal.fire("No data", "No rows available.", "info");
        return;
    }

    const wb = XLSX.utils.book_new();

    Object.keys(allAgentsData).forEach(agentId => {
        const data = allAgentsData[agentId];

        const ws = XLSX.utils.json_to_sheet(data);
        const agentObj = agents.find(a => a.id === agentId);

        XLSX.utils.book_append_sheet(
        wb,
        ws,
        agentObj?.name?.substring(0, 25) || agentId // Excel sheet name max 31 chars
        );
    });

    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "vonage_all_agents.xlsx");
    };


  // OVERALL SUMMARY (across all agents) – used only when mode === "all_agents"
  const overallReceived = useMemo(
    () => rows.filter((r) => normStatus(r.status) === "answered").length,
    [rows]
  );
  const overallMissed = useMemo(
    () => rows.filter((r) => normStatus(r.status) === "missed").length,
    [rows]
  );
  const overallActualMissed = useMemo(
    () =>
      rows.filter(
        (r) => normStatus(r.status) === "missed" && r.actualMissed === true
      ).length,
    [rows]
  );
  const overallAbandoned = useMemo(
    () => rows.filter((r) => r.abandoned === true).length,
    [rows]
  );
  const overallDuration = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.durationSec) || 0), 0),
    [rows]
  );

  // Polling logic (no page text, only generic processing)
  const pollUntilReady = async (jobId) => {
    for (;;) {
      let data;
      try {
        ({ data } = await axios.get(FS_STATUS(jobId)));
      } catch (e) {
        await new Promise((r) => setTimeout(r, 1500));
        ({ data } = await axios.get(FS_STATUS(jobId)));
      }

      const s = data?.status?.status;

      if (s === "completed" || s === "done") return true;
      if (s === "error")
        throw new Error(data.status.message || "Processing failed");

      await new Promise((r) => setTimeout(r, 1500));
    }
  };

  // 🚨 Friendly validation before request
  const validate = () => {
    const newErrors = {
      agentId: false,
      phone: false,
      fromDate: false,
      toDate: false,
    };
    const missing = [];

    if (mode === "agent") {
      if (!agentId) {
        newErrors.agentId = true;
        missing.push("Agent");
      }
    } else if (mode === "phone") {
      if (!phone) {
        newErrors.phone = true;
        missing.push("Phone Number");
      }
    }
    // mode === "all_agents": no individual agent or phone required

    if (!fromDate) {
      newErrors.fromDate = true;
      missing.push("From Date");
    }
    if (!toDate) {
      newErrors.toDate = true;
      missing.push("To Date");
    }

    setFieldErrors(newErrors);

    if (missing.length > 0) {
      Swal.fire(
        "Missing fields",
        `Please fill: ${missing.join(", ")}`,
        "warning"
      );
      return false;
    }
    return true;
  };

  const assignResults = (results) => {
    const safe = results || [];
    setRows(safe);

    if (mode === "all_agents") {
      const grouped = {};
      safe.forEach((r) => {
        const aid = r.agentId || r.agent_id || "";
        if (!aid) return;
        if (!grouped[aid]) grouped[aid] = [];
        grouped[aid].push(r);
      });
      setAllAgentsData(grouped);
      const firstKey = Object.keys(grouped)[0] || "";
      setActiveAgentTab(firstKey);
    } else {
      setAllAgentsData({});
      setActiveAgentTab("");
    }
  };

  const fetchData = async () => {
    if (!validate()) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const agentIdsArray = agents.map(a => a.id);
      const params =
        mode === "agent"
          ? {
              agentId,
              from: fromDate + " 00:00:00",
              to: toDate + " 23:59:59",
              status,
            }
          : mode === "all_agents"
          ? {
              agentId: "all",
              agents: JSON.stringify(agentIdsArray),
              from: fromDate + " 00:00:00",
              to: toDate + " 23:59:59",
              status,
            }
          : {
              phone,
              from: fromDate + " 00:00:00",
              to: toDate + " 23:59:59",
            };

      const url = mode === "phone" ? FS_CALLER : FS_AGENT;
      const { data } = await axios.get(url, { params });

      if (data.processing && data.jobId) {
        // 🔄 Modern processing modal
        Swal.fire({
          title: "Processing your request...",
          text: "Please wait while we fetch the call logs.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        await pollUntilReady(data.jobId);

        Swal.close();
        const doneResp = await axios.get(url, { params });
        assignResults(doneResp.data.results);
      } else {
        assignResults(data.results);
      }

      setPage(1);
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || err.message,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // 📤 Export to CSV (current filtered rows of active agent / mode)
  const exportCSV = () => {
    if (!filteredRows.length) {
      Swal.fire("No data", "No rows available to export.", "info");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(filteredRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Calls");

    const csvOutput = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "vonage_calls.csv");
  };

  // 📘 Export to Excel
  const exportExcel = () => {
    if (!filteredRows.length) {
      Swal.fire("No data", "No rows available to export.", "info");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(filteredRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Calls");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "vonage_calls.xlsx");
  };

  {/* 🔵 CALL FLOW TIMELINE MODAL */}
    


  return (
    <>
    <CallFlowModal
      open={flowOpen}
      onClose={() => setFlowOpen(false)}
      flowData={flowData}
      fmtTimeOnly={fmtTimeOnly}
      normStatus={normStatus}
    />

    
    <Box
      p={4}
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fb 0%, #e3f2fd 100%)",
      }}
    >
      <Card
        elevation={4}
        sx={{
          borderRadius: 3,
          maxWidth: 1300,
          mx: "auto",
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        {/* Top loader bar */}
        {loading && <LinearProgress />}

        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              📞 Vonage Call Analytics
            </Typography>
          </Box>

          {/* 🔍 INPUT CONTROLS */}
          <Grid
            container
            spacing={2}
            my={1}
            sx={{
              backgroundColor: "#fafafa",
              borderRadius: 2,
              p: 2,
              mb: 2,
              border: "1px solid #e0e0e0",
            }}
          >
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Search Mode"
                value={mode}
                onChange={(e) => {
                  const value = e.target.value;
                  setMode(value);
                  setFieldErrors((prev) => ({
                    ...prev,
                    agentId: false,
                    phone: false,
                  }));
                  if (value === "all_agents") {
                    setAgentId("");
                  }
                }}
                size="small"
              >
                <MenuItem value="agent">Search by Agent</MenuItem>
                <MenuItem value="all_agents">Search by ALL Agents</MenuItem>
                {/* <MenuItem value="phone">Search by Phone</MenuItem> */}
              </TextField>
            </Grid>

            {mode === "agent" && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Agent"
                  value={agentId}
                  onChange={(e) => {
                    setAgentId(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, agentId: false }));
                  }}
                  error={fieldErrors.agentId}
                  helperText={fieldErrors.agentId ? "Agent is required" : ""}
                  size="small"
                >
                  {agents.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {mode === "phone" && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, phone: false }));
                  }}
                  error={fieldErrors.phone}
                  helperText={
                    fieldErrors.phone ? "Phone Number is required" : ""
                  }
                  size="small"
                />
              </Grid>
            )}

            <Grid item xs={6} sm={2}>
              <TextField
                label="From"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, fromDate: false }));
                }}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                error={fieldErrors.fromDate}
                helperText={fieldErrors.fromDate ? "From Date is required" : ""}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="To"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, toDate: false }));
                }}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                error={fieldErrors.toDate}
                helperText={fieldErrors.toDate ? "To Date is required" : ""}
              />
            </Grid>

            {(mode === "agent" || mode === "all_agents") && (
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  size="small"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="answered">Received</MenuItem>
                  <MenuItem value="missed">Missed</MenuItem>
                  <MenuItem value="actual_missed">Actual Missed</MenuItem>
                  {/* <MenuItem value="abandoned">Abandoned</MenuItem> */}
                </TextField>
              </Grid>
            )}

            <Grid item xs={12} sm={8}>
              <Button
                variant="contained"
                fullWidth
                onClick={fetchData}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: "bold",
                  borderRadius: 2,
                  py: 1.2,
                  boxShadow: "none",
                  background:
                    "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                  "&:hover": {
                    boxShadow: 4,
                    transform: "translateY(-1px)",
                    background:
                      "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Search"
                )}
              </Button>
            </Grid>
          </Grid>

          {/* 🔍 SEARCH FILTER */}
          <TextField
            sx={{ my: 2 }}
            fullWidth
            placeholder="Search within results… (phone, agent, status, etc.)"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
          />

          {/* Overall summary (only when all_agents) */}
          {mode === "all_agents" && rows.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: "#e3f2fd",
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle2">
                🌐 Overall Received: <b>{overallReceived}</b>
              </Typography>
              <Typography variant="subtitle2">
                Overall Missed: <b>{overallMissed}</b>
              </Typography>
              <Typography variant="subtitle2">
                Overall Actual Missed: <b>{overallActualMissed}</b>
              </Typography>
              <Typography variant="subtitle2">
                Overall Abandoned: <b>{overallAbandoned}</b>
              </Typography>
              <Typography variant="subtitle2">
                Overall Duration: <b>{formatTotalDuration(overallDuration)}</b>
              </Typography>
            </Box>
          )}

          {/* ⭐ AGENT TABS (all_agents mode) */}
          {mode === "all_agents" &&
            Object.keys(allAgentsData).length > 0 && (
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                  value={activeAgentTab}
                  onChange={(e, val) => {
                    setActiveAgentTab(val);
                    setPage(1);
                  }}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                >
                  {Object.keys(allAgentsData).map((agentKey) => {
                    const agentObj = agents.find((a) => a.id === agentKey);
                    return (
                      <Tab
                        key={agentKey}
                        label={agentObj?.name || agentKey}
                        value={agentKey}
                      />
                    );
                  })}
                </Tabs>
              </Box>
            )}

          {/* ⭐ STICKY SUMMARY CARDS (current agent / current view) */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              backgroundColor: "#fff",
              pb: 1,
              mb: 2,
            }}
          >
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={2.4}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "#e8f5e9",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <Typography>✅ Received</Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {totalReceived}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} md={2.4}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "#ffebee",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <Typography>❌ Missed</Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {totalMissed}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} md={2.4}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "#ffcdd2",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <Typography>🎯 Actual Missed</Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {totalActualMissed}
                  </Typography>
                </Card>
              </Grid>

              {/* 🆕 TOTAL DURATION */}
              <Grid item xs={12} md={2.4}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: "#fff3e0",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <Typography>⏱ Total Calls Duration</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {formatTotalDuration(totalDuration)}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* 📈 MINI CHART (for current view: agent or active tab) */}
          {chartData.length > 0 && (
            <Box sx={{ height: 260, mb: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="answered" name="Answered" />
                  <Line type="monotone" dataKey="missed" name="Missed" />
                  <Line
                    type="monotone"
                    dataKey="actualMissed"
                    name="Actual Missed"
                  />
                  {/* <Line
                    type="monotone"
                    dataKey="abandoned"
                    name="Abandoned"
                  /> */}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Export buttons */}
          {/* <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                onClick={exportCSV}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  "&:hover": { boxShadow: 2 },
                }}
              >
                Export CSV
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="success"
                onClick={exportExcel}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  "&:hover": { boxShadow: 2 },
                }}
              >
                Export Excel
              </Button>
            </Grid>
          </Grid> */}
          {/* Export buttons */}
         <Grid container spacing={2} sx={{ mb: 2 }}>

            {/* Export current view CSV */}
            <Grid item>
                <Button
                variant="outlined"
                color="primary"
                onClick={exportCSV}
                sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
                >
                Export CSV (Current View)
                </Button>
            </Grid>

            {/* Export current view Excel */}
            <Grid item>
                <Button
                variant="outlined"
                color="success"
                onClick={exportExcel}
                sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
                >
                Export Excel (Current View)
                </Button>
            </Grid>

            {/* EXPORT ALL AGENTS */}
            {mode === "all_agents" && rows.length > 0 && (
                <>
                <Grid item>
                    <Button
                    variant="outlined"
                    color="secondary"
                    onClick={exportAllAgentsCSV}
                    sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
                    >
                    Export ALL Agents CSV
                    </Button>
                </Grid>

                <Grid item>
                    <Button
                    variant="outlined"
                    color="warning"
                    onClick={exportAllAgentsExcel}
                    sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
                    >
                    Export ALL Agents Excel
                    </Button>
                </Grid>
                </>
            )}
         </Grid>

          {/* 🧾 NO RESULTS STATE */}
          {hasSearched && !loading && filteredRows.length === 0 && (
            <Paper
              sx={{
                p: 4,
                textAlign: "center",
                borderRadius: 2,
                mb: 2,
                backgroundColor: "#fafafa",
                border: "1px dashed #b0bec5",
              }}
            >
              <Typography variant="h6" gutterBottom>
                No matching calls found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting the date range, status, agent, or search text.
              </Typography>
            </Paper>
          )}

          {/* 📋 TABLE */}
          {filteredRows.length > 0 && (
            <Box
              sx={{
                animation: "fadeIn 0.25s ease-in-out",
                "@keyframes fadeIn": {
                  from: { opacity: 0, transform: "translateY(4px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        backgroundColor: "#e3f2fd",
                      }}
                    >
                      {[
                        "#",
                        "Time (UTC)",
                        "From",
                        "To",
                        "Status",
                        "Actual Missed?",
                        "Duration",
                        "Agent", "Flow"
                      ].map((label, idx) => (
                        <TableCell
                          key={idx}
                          align="center"
                          sx={{
                            backgroundColor: "#e3f2fd !important",
                            fontWeight: "bold",
                            cursor: ["Time (UTC)", "From", "To", "Duration", "Agent"].includes(
                              label
                            )
                              ? "pointer"
                              : "default",
                            borderBottom: "1px solid #bbdefb",
                          }}
                          onClick={() => {
                            if (label === "Time (UTC)") handleSort("startedAt");
                            if (label === "From") handleSort("from");
                            if (label === "To") handleSort("to");
                            if (label === "Duration") handleSort("durationSec");
                            if (label === "Agent") handleSort("agentName");
                          }}
                        >
                          {label}
                          {label === "Time (UTC)" &&
                            sortField === "startedAt" &&
                            (sortOrder === "asc" ? " ⬆" : " ⬇")}
                          {label === "From" &&
                            sortField === "from" &&
                            (sortOrder === "asc" ? " ⬆" : " ⬇")}
                          {label === "To" &&
                            sortField === "to" &&
                            (sortOrder === "asc" ? " ⬆" : " ⬇")}
                          {label === "Duration" &&
                            sortField === "durationSec" &&
                            (sortOrder === "asc" ? " ⬆" : " ⬇")}
                          {label === "Agent" &&
                            sortField === "agentName" &&
                            (sortOrder === "asc" ? " ⬆" : " ⬇")}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedRows.map((r, i) => {
                      const baseColor = getRowColor(r);
                      return (
                        <TableRow
                          key={r.callId || i}
                          sx={{
                            ...baseColor,
                            transition:
                              "background-color 0.2s ease, transform 0.1s ease",
                            "&:hover": {
                              backgroundColor: "#e3f2fd",
                              boxShadow: "0 0 6px rgba(0,0,0,0.1)",
                            },
                          }}
                        >
                          <TableCell align="center">
                            {(page - 1) * limit + i + 1}
                          </TableCell>
                          <TableCell align="center">
                            {fmtUTC(r.startedAt)}
                          </TableCell>
                          <TableCell align="center">{r.from}</TableCell>
                          <TableCell align="center">{r.to}</TableCell>

                          <TableCell align="center">
                            <Chip
                              label={normStatus(r.status)}
                              color={statusColor[normStatus(r.status)]}
                              size="small"
                              sx={{
                                textTransform: "capitalize",
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>

                          <TableCell align="center">
                            {normStatus(r.status) === "missed"
                              ? r.actualMissed
                                ? "✔"
                                : "✖"
                              : "-"}
                          </TableCell>

                          <TableCell align="center">
                            {secondsToHMS(r.durationSec)}
                          </TableCell>
                          <TableCell align="center">
                            {r.agentName || r.agentId}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => showCallFlow(r.from || r.to, r.startedAt)}
                                sx={{ textTransform: "none" }}
                            >
                                Flow
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* PAGINATION */}
          {filteredRows.length > 0 && (
            <Stack mt={2} alignItems="center">
              <Pagination
                count={Math.ceil(filteredRows.length / limit)}
                page={page}
                onChange={(e, val) => setPage(val)}
              />
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
    </>
  );
}
